import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  GridColDef,
  GridFooter,
  GridFooterContainer,
  GridSearchIcon,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import React from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { useDebounce, usePrevious } from 'react-use';
import { useCompany } from '@dispatch/shared/hooks';
import { ResourceMeta } from '@dispatch/shared/types';
import TableComponent from '../../table-component/Table';
import {
  tableStyleHideSeparator,
  tableStyleOverrides,
} from '../../table-component/tableStyle';
import { ReasonModal } from '../ReasonModal';
import { TaskDetailsContext } from '@dispatch/web/hooks';

export interface ResourceDataGrid {
  resources: any;
  resourceMeta?: ResourceMeta;
}

export const ResourceDataGrid = ({
  resources,
  resourceMeta,
}: ResourceDataGrid) => {
  const [selectedResources, setSelectedResources] = React.useState<string>();
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (row) =>
        row?.row?.name || row?.row?.resourceName || row?.row?.ResourceName,
    },
    {
      field: 'assign',
      headerName: '',
      width: 130,
      renderCell: (row) => (
        <Button
          data-testid="assign-resource-button"
          onClick={() => {
            setSelectedResources(
              row?.row?.Id ?? row?.row?.id ?? row?.row?.integrationGUID
            );
            resourceMeta?.setModalState?.({ open: true });
          }}
          variant="contained"
          size="small"
          sx={{
            alignSelf: 'center',
          }}
        >
          Assign
        </Button>
      ),
      sortable: false,
    },
  ];

  const taskContext = React.useContext(TaskDetailsContext);

  function customFooter() {
    return (
      <GridFooterContainer>
        <Stack direction="row" alignItems={'center'}>
          <Switch
            id="showQualified"
            name="showQualified"
            checked={taskContext?.value ?? resourceMeta?.showOnlyQualified}
            onChange={() => {
              taskContext?.setShowOnlyQualified(!taskContext.value) ??
                resourceMeta?.setShowOnlyQualified?.(
                  !resourceMeta?.showOnlyQualified
                );
            }}
          />
          <p>Show Only Qualified</p>
        </Stack>
        <GridFooter />
      </GridFooterContainer>
    );
  }

  const [qualifiedSearch, setQualifiedSearch] = React.useState<string>('');
  const [debouncedVal, setDebouncedVal] = React.useState('');
  const hasPreviousValue = usePrevious(debouncedVal);
  const [, cancel] = useDebounce(
    () => {
      setDebouncedVal?.(qualifiedSearch);
    },
    1000,
    [qualifiedSearch]
  );

  React.useEffect(() => {
    if (debouncedVal !== '') {
      resourceMeta?.setResourceFilters?.({
        pageSize: 10,
        resourceName: debouncedVal,
      });
    }

    return cancel;
  }, [debouncedVal, cancel]);

  React.useEffect(() => {
    if (hasPreviousValue && qualifiedSearch === '') {
      resourceMeta?.setResourceFilters?.({
        resourceName: '',
        pageSize: 10,
      });
    }
  }, [qualifiedSearch]);

  const noResultsOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center">
      Filter Returned No Resources
    </Stack>
  );

  const isLoading = resources?.status === 'loading';

  const noRowsOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center">
      {isLoading ? (
        <CircularProgress data-testid="qualified-resources-loader" />
      ) : taskContext?.setShowOnlyQualified ||
        resourceMeta?.showOnlyQualified ? (
        'No Qualified Resources Found'
      ) : (
        'No Resources'
      )}
    </Stack>
  );

  return (
    <>
      <TableComponent
        tableHeight="100%"
        emptyEntityName="Resource"
        // Pass 'empty' while loading so DataGridPro renders NoRowsOverlay
        // instead of its built-in LoadingOverlay — we show our own spinner inside NoRowsOverlay.
        status={isLoading ? 'empty' : resources?.status}
        getRowId={(row: any) => row?.integrationGUID || row?.id || row?.Id}
        rows={resources?.data ?? []}
        dataGridProps={{
          sx: [tableStyleHideSeparator, tableStyleOverrides],
          rowHeight: 40,
          headerHeight: 35,
          disableMultipleSelection: true,
          hideFooterSelectedRowCount: true,
          disableDensitySelector: true,
          disableColumnFilter: true,
          components: {
            Toolbar: !resourceMeta?.showOnlyQualified
              ? QuickSearchToolbar
              : undefined,
            Footer: customFooter,
            NoRowsOverlay: noRowsOverlay,
            NoResultsOverlay: noResultsOverlay,
          },
          componentsProps: {
            toolbar: {
              value: qualifiedSearch,
              quickFilterProps: { debounceMs: 500 },
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                setQualifiedSearch(event.target.value),
              clearSearch: () => setQualifiedSearch(''),
            },
          },
        }}
        columns={columns}
        serverPaging={
          !resourceMeta?.showOnlyQualified && {
            ...resources,
            hasNextPage:
              resources?.pages?.[resources?.pages?.length - 1]
                .continuationToken,
            onPageSizeChange: (newPageSize) => {
              resourceMeta?.setResourceFilters?.({ pageSize: newPageSize });
            },
          }
        }
      />
      <ReasonSelectModal
        selectedResource={selectedResources}
        resourceMeta={resourceMeta}
        setModalState={resourceMeta?.setModalState}
      />
    </>
  );
};

interface QuickSearchToolbarProps {
  clearSearch: () => void;
  onChange: () => void;
  value: string;
}

function QuickSearchToolbar(props: QuickSearchToolbarProps) {
  return (
    <div style={{ padding: '10px', float: 'right' }}>
      <div>
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </div>
      <TextField
        variant="standard"
        value={props.value}
        onChange={props.onChange}
        placeholder="Search…"
        InputProps={{
          startAdornment: <GridSearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear"
              size="small"
              style={{ visibility: props.value ? 'visible' : 'hidden' }}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
    </div>
  );
}

const ReasonSelectModal = ({
  selectedResource,
  resourceMeta,
  setModalState,
}: {
  selectedResource?: string;
  resourceMeta?: ResourceMeta;
  setModalState?: React.Dispatch<{
    open: boolean;
    callback?: (e: any) => void;
  }>;
}) => {
  const { rescheduleReasons } = useCompany();

  return (
    <ReasonModal
      reasonOptions={rescheduleReasons}
      modalState={resourceMeta?.modalState || { open: false }}
      setModalState={setModalState}
      onClose={() => {
        setModalState?.({ open: false });
      }}
      onSubmit={(reasonOption: string, forceReassign?: boolean) => {
        if (reasonOption) {
          resourceMeta?.handleReassign?.({
            resourceId: selectedResource ?? '',
            reasonCode: reasonOption,
            task: resourceMeta?.taskToAssign,
            forceReassign: forceReassign ?? false,
          });
          setModalState?.({ open: false });
        } else {
          resourceMeta?.notifyError?.(
            'Error',
            'Must choose a reason for rescheduling!'
          );
        }
      }}
      title={'Please select a reason'}
      inputLabel="Assign Reason"
      needForceReassign={true}
    />
  );
};
